import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import Pagination from '../../components/Pagination';
import AlertDialog from '../../components/AlertDialog';
import {
  VERIFY_CODE_PAGE_SIZE,
  VERIFY_CODE_ROLE_OPTIONS,
  buildVerifyCodePageParams,
  formatVerifyCodeRole,
  formatVerifyCodeScene,
  formatVerifyCodeType,
  formatVerifyDevice,
} from './verifyCodeConstants';

const EMPTY_FILTERS = {
  roleId: '',
  memberId: '',
  verifySend: '',
};

const TABLE_COLUMNS = [
  '場景',
  '用戶ID',
  '帳號類型',
  '類型',
  '手機國名',
  '區號',
  '用戶信箱/手機號',
  '驗證碼',
  '用戶登入設備',
  '客戶端IP',
  '發送時間',
];

export default function VerificationCodeManagePage() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [creatingCommonCode, setCreatingCommonCode] = useState(false);
  const [commonCodeAlert, setCommonCodeAlert] = useState(null);

  async function loadRecords(pageNo = 1, nextFilters = filters) {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.verifyCodeRecordPage(
        buildVerifyCodePageParams(pageNo, nextFilters)
      );
      setRecords(data?.records || []);
      setPage(data?.current || pageNo);
      setTotalPages(data?.pages || 1);
      setTotal(data?.total ?? 0);
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRecords(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function handleSearch(event) {
    event.preventDefault();
    setMessage('');
    loadRecords(1, filters);
  }

  function handleReset() {
    const reset = { ...EMPTY_FILTERS };
    setFilters(reset);
    setMessage('');
    loadRecords(1, reset);
  }

  async function handleCreateCommonCode() {
    setCreatingCommonCode(true);
    setError('');
    setMessage('');
    try {
      const code = await adminApi.createDisposableCommonVerifyCode();
      setCommonCodeAlert({
        title: '通用驗證碼',
        message: `一次性通用驗證碼：${code}（有效時間內僅可使用一次，校驗成功後自動失效）`,
      });
    } catch (err) {
      setError(err.message || '產生失敗');
    } finally {
      setCreatingCommonCode(false);
    }
  }

  return (
    <div className="page-card">
      <div className="section-header">
        <h2>驗證碼管理</h2>
      </div>

      <form className="filter-bar filter-bar-wrap" onSubmit={handleSearch}>
        <div className="form-field">
          <label htmlFor="verifyCodeFilterRoleId">帳號類型</label>
          <select
            id="verifyCodeFilterRoleId"
            value={filters.roleId}
            onChange={(e) => updateFilter('roleId', e.target.value)}
          >
            {VERIFY_CODE_ROLE_OPTIONS.map((option) => (
              <option key={option.value || 'all'} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-field">
          <label htmlFor="verifyCodeFilterMemberId">用戶ID</label>
          <input
            id="verifyCodeFilterMemberId"
            type="text"
            value={filters.memberId}
            onChange={(e) => updateFilter('memberId', e.target.value)}
            placeholder="請輸入用戶ID"
          />
        </div>
        <div className="form-field">
          <label htmlFor="verifyCodeFilterVerifySend">用戶信箱/手機號</label>
          <input
            id="verifyCodeFilterVerifySend"
            type="text"
            value={filters.verifySend}
            onChange={(e) => updateFilter('verifySend', e.target.value)}
            placeholder="請輸入用戶信箱/手機號"
          />
        </div>
        <div className="filter-actions filter-actions--end">
          <button type="submit" className="btn btn-primary">
            搜尋
          </button>
          <button type="button" className="btn btn-secondary" onClick={handleReset}>
            重置
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={creatingCommonCode}
            onClick={handleCreateCommonCode}
          >
            {creatingCommonCode ? '產生中…' : '獲取通用驗證碼'}
          </button>
        </div>
      </form>

      {message ? <p className="form-message form-message--success">{message}</p> : null}
      {error ? <p className="form-message form-message--error">{error}</p> : null}

      <div className="table-meta">
        共 {total} 筆，每頁 {VERIFY_CODE_PAGE_SIZE} 筆
      </div>

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              {TABLE_COLUMNS.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="table-empty">
                  載入中…
                </td>
              </tr>
            ) : records.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="table-empty">
                  無資料
                </td>
              </tr>
            ) : (
              records.map((row) => (
                <tr key={row.id}>
                  <td>{formatVerifyCodeScene(row.scene)}</td>
                  <td>{row.memberId ?? '—'}</td>
                  <td>{formatVerifyCodeRole(row.roleId)}</td>
                  <td>{formatVerifyCodeType(row.verifyType)}</td>
                  <td>{row.phoneCountryNameCn || '—'}</td>
                  <td>{row.phoneAreaCode || '—'}</td>
                  <td>{row.verifySend || '—'}</td>
                  <td className="mono-cell">{row.verifyCode || '—'}</td>
                  <td>{formatVerifyDevice(row.device)}</td>
                  <td>{row.memberIp || '—'}</td>
                  <td>{formatDateTime(row.createTime)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={(next) => loadRecords(next, filters)} />

      <AlertDialog
        open={Boolean(commonCodeAlert)}
        title={commonCodeAlert?.title}
        message={commonCodeAlert?.message}
        onClose={() => setCommonCodeAlert(null)}
      />
    </div>
  );
}
